import Vue, { CreateElement } from 'src/vue'
import { Component, Prop } from 'vue-property-decorator'
import ClickOutside from 'vue-click-outside'

import { Button } from '@src/elements/button/button'
import { Icon } from '@src/elements/icon/icon'
import { Input } from '@src/elements/input/input'

import type { ScreensaverDTO } from '@src/dto/screensaver-dto'
import { DEFAULT_SCREENSAVER } from '@src/data/station'
import { SUPPORTED_IMAGE_TYPES, SUPPORTED_VIDEO_TYPES, SCREENSAVER_SIZE_LIMIT } from '@src/util/constants'
import { validateSupportedFileType } from '@src/util/validation'
import { Modal } from '@src/elements/modal/modal'

import './screensaver.sass'

const SUPPORTED_IMAGE_FORMATS = [...SUPPORTED_IMAGE_TYPES, ...SUPPORTED_VIDEO_TYPES].map((item) => item.toUpperCase()).join(', ')
const MAXIMUM_SIZE = '10 MB'
const RECOMMENDED_RESOLUTION = {
  width: 1080,
  height: 1920,
  dpi: 72
}

@Component({
  directives: {
    ClickOutside
  }
})
export class Screensaver extends Vue {

  @Prop({ required: true })
  private initial: ScreensaverDTO

  @Prop({ required: false, default: null })
  private selectedForUpload: ScreensaverDTO | null

  @Prop({ required: true })
  private saveCallback: (screensaver: ScreensaverDTO | null) => void

  @Prop({ required: true })
  private closeCallback: () => void

  private selectedScreensaver: ScreensaverDTO | null = null
  private fileToUpload: ScreensaverDTO | null = null

  constructor () {
    super()
  }

  created () {
    this.selectedScreensaver = this.selectedForUpload || this.initial
    this.fileToUpload = this.selectedForUpload && this.initial.content === this.selectedForUpload.content ? null : this.selectedForUpload
  }

  selectDefault () {
    this.selectedScreensaver = DEFAULT_SCREENSAVER
  }

  selectInitial () {
    this.selectedScreensaver = this.initial
  }

  selectNewFile () {
    if (this.fileToUpload) {
      this.selectedScreensaver = this.fileToUpload
    }
  }

  save () {
    this.saveCallback(this.selectedScreensaver)
  }

  onFileChange (e) {
    let files = e.target.files || e.dataTransfer.files
    if (!files.length) {
      return
    }

    // build the object to be displayed
    let screensaverDTO: ScreensaverDTO = {
      content: '',
      filename: files[0].name,
      content_type: files[0].type
    }

    if (files[0].size > SCREENSAVER_SIZE_LIMIT) {
      this.$store.dispatch('showToast', { message: this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.SCREEN_SAVER.SIZE_ERROR'), fadeOut: false })
      return
    }

    // validate file format
    const isValidVideo = validateSupportedFileType(files[0].type, SUPPORTED_VIDEO_TYPES)
    const isValidImage = validateSupportedFileType(files[0].type, SUPPORTED_IMAGE_TYPES)

    if (isValidVideo || isValidImage) {
      let reader = new FileReader()
      reader.readAsDataURL(files[0])
      reader.onload = () => {
        // image as base64
        screensaverDTO.content = reader.result as string
        this.$store.dispatch('closeToast')
        this.fileToUpload = screensaverDTO
        this.selectNewFile()
      }
    } else {
      this.$store.dispatch('showToast', { message: this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.SCREEN_SAVER.ERROR'), fadeOut: false })
    }
  }

  render (h: CreateElement) {
    const onlyDefaultIsDisplayed = this.initial === DEFAULT_SCREENSAVER && !this.fileToUpload
    return <Modal id={'screensaver-modal'} show={true} closeCallback={this.closeCallback} title={this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.SCREEN_SAVER.SELECT_NEW')}>
      <div class='image-requirements'>
        <div>
        {this.$t('IMAGE_REQUIREMENTS.RECOMMENDED_IMAGE_RESOLUTION_TEXT')}&nbsp;
          <b>{this.$t('IMAGE_REQUIREMENTS.RECOMMENDED_IMAGE_RESOLUTION_VALUE', {
            width: RECOMMENDED_RESOLUTION.width,
            height: RECOMMENDED_RESOLUTION.height,
            dpi: RECOMMENDED_RESOLUTION.dpi
          })}</b>
        </div>
        <div>{this.$t('IMAGE_REQUIREMENTS.RECOMMENDED_IMAGE_SIZE_TEXT')}&nbsp; <b>{MAXIMUM_SIZE}</b></div>
        <div>{this.$t('IMAGE_REQUIREMENTS.SUPPORTED_FILE_FORMATS_TEXT')}&nbsp; <b>{SUPPORTED_IMAGE_FORMATS}</b></div>
      </div>
      <div class={`list popup-content screensavers ${onlyDefaultIsDisplayed && 'justifyCenter'}`}>
        {this.renderDefault(h)}
        {this.renderUploaded(h)}
        {this.renderSelectedForUpload(h)}
        {this.renderUploadForm(h)}
      </div>
      <div class='btn-row' slot='footer'>
        <Button type='outline' label={this.$t('CANCEL')} click={this.closeCallback} ref='closeBtn' />
        <Button class='save-btn' label={this.$t('SAVE')} click={this.save} ref='saveBtn' />
      </div>
    </Modal>
  }

  // Default: clock
  renderDefault (h: CreateElement) {
    return this.renderPreview(h, DEFAULT_SCREENSAVER, this.selectDefault)
  }

  // Image/video currently set
  renderUploaded (h: CreateElement) {
    if (!this.initial || this.initial === DEFAULT_SCREENSAVER) {
      return
    }

    return this.renderPreview(h, this.initial, this.selectInitial)
  }

  // Image/video selected for upload
  renderSelectedForUpload (h: CreateElement) {
    if (!this.fileToUpload || this.fileToUpload.content === DEFAULT_SCREENSAVER.content) {
      return
    }

    return this.renderPreview(h, this.fileToUpload, this.selectNewFile)
  }

  // render the screensaver preview
  renderPreview (h: CreateElement, screensaver: ScreensaverDTO, selectCallback: () => void) {
    return <div class='screensaver-preview' onClick={selectCallback}>
      {screensaver.content_type.includes('video') ?
        <video class='screensaver' muted src={screensaver.content} />
        : <img class='screensaver' src={screensaver.content} />}
      <img class='station-border' src={'/assets/icon/brand/station-border.png'} />
      {this.selectedScreensaver && screensaver.content === this.selectedScreensaver.content ? <Icon name='util/selected' /> : null}
    </div>
  }

  // Button to select file from disk
  renderUploadForm (h: CreateElement) {
    return <div class='upload-form'>
      <Icon name='util/add' />
      <Input ref='file-upload' htmlId='screensaver' type='file' fileChanged={this.onFileChange} />
    </div>
  }
}
